import { useMemo, memo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useDateRange } from '../../contexts/DateRangeContext';
import { useOutageHistory, useCommunityHistory, useWeatherHistory } from '../../hooks/useHistoricalData';
import { LoadingSkeleton } from '../../components/atoms/LoadingSkeleton/LoadingSkeleton';
import { Card } from '../../components/atoms/Card/Card';
import { FullscreenChart, useIsFullscreen } from '../../components/molecules/FullscreenChart/FullscreenChart';
import { CHART_CONFIG, shouldAnimate } from '../../constants';
import type { CommunityTimeSeries } from '../../../types/outage';
import type { WeatherAverage } from '../../../types/weather';
import './OutageTimelineChart.css';

// Cache for formatted time strings to avoid repeated expensive toLocaleString calls
const timeFormatCache = new Map<string, string>();

// Helper to format timestamps for chart display with caching
function formatChartTime(ts: string, todayDateStr: string): string {
  const cacheKey = `${ts}-${todayDateStr}`;
  const cached = timeFormatCache.get(cacheKey);
  if (cached) return cached;

  const date = new Date(ts);
  const isToday = date.toLocaleDateString("en-US", { timeZone: "America/Halifax" }) === todayDateStr;

  let result: string;
  if (isToday) {
    result = date.toLocaleTimeString("en-US", {
      timeZone: "America/Halifax",
      hour: "numeric",
      minute: "2-digit",
    });
  } else {
    result = date.toLocaleString("en-US", {
      timeZone: "America/Halifax",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }

  // Limit cache size to prevent memory leaks
  if (timeFormatCache.size > 5000) {
    timeFormatCache.clear();
  }
  timeFormatCache.set(cacheKey, result);
  return result;
}

// Binary search to find nearest weather reading - O(log n) instead of O(n)
function findNearestWeather(
  sortedWeather: Array<{ timestamp: number; data: WeatherAverage }>,
  targetTime: number
): WeatherAverage | undefined {
  if (sortedWeather.length === 0) return undefined;
  if (sortedWeather.length === 1) return sortedWeather[0].data;

  let left = 0;
  let right = sortedWeather.length - 1;

  // Binary search for closest timestamp
  while (left < right - 1) {
    const mid = Math.floor((left + right) / 2);
    if (sortedWeather[mid].timestamp <= targetTime) {
      left = mid;
    } else {
      right = mid;
    }
  }

  // Compare left and right to find nearest
  const leftDiff = Math.abs(sortedWeather[left].timestamp - targetTime);
  const rightDiff = Math.abs(sortedWeather[right].timestamp - targetTime);

  return leftDiff <= rightDiff ? sortedWeather[left].data : sortedWeather[right].data;
}

// Inner component that consumes fullscreen context - memoized to prevent unnecessary re-renders
interface OutageChartContentProps {
  chartData: Array<{
    timestamp: string;
    time: string;
    outages: number;
    avgTemp?: number;
    avgWind?: number;
    scaledTemp?: number;
    scaledWind?: number;
  }>;
  animationsEnabled: boolean;
}

const OutageChartContent = memo(function OutageChartContent({ chartData, animationsEnabled }: OutageChartContentProps) {
  const isFullscreen = useIsFullscreen();

  return (
    <ResponsiveContainer width="100%" height={isFullscreen ? "100%" : 300}>
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" stroke={CHART_CONFIG.gridStroke} />
        <XAxis
          dataKey="time"
          stroke={CHART_CONFIG.axisStroke}
          tick={{ fill: CHART_CONFIG.axisStroke }}
        />
        <YAxis
          yAxisId="left"
          stroke="#f85149"
          tick={{ fill: CHART_CONFIG.axisStroke }}
          label={{
            value: "Customers",
            angle: -90,
            position: "insideLeft",
            fill: CHART_CONFIG.axisStroke,
          }}
          domain={["auto", "auto"]}
          tickCount={10}
        />
        <Tooltip
          contentStyle={CHART_CONFIG.tooltipStyle}
          formatter={(value: number, name: string, props: { payload?: { avgTemp?: number; avgWind?: number } }) => {
            if (name === "Customer Outages") {
              return [`${Math.round(value)} customers`, name];
            }
            if (name === "Avg Temperature") {
              const realTemp = props.payload?.avgTemp;
              return realTemp != null
                ? [`${Math.round(realTemp)}°C`, "Avg Temperature"]
                : null;
            }
            if (name === "Avg Wind Speed") {
              const realWind = props.payload?.avgWind;
              return realWind != null
                ? [`${Math.round(realWind)} km/h`, "Avg Wind Speed"]
                : null;
            }
            return [Math.round(value), name];
          }}
        />
        <Legend wrapperStyle={{ color: CHART_CONFIG.axisStroke }} />
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="outages"
          stroke="#f85149"
          strokeWidth={CHART_CONFIG.strokeWidth}
          dot={false}
          name="Customer Outages"
          isAnimationActive={animationsEnabled}
          animationDuration={CHART_CONFIG.animation.duration}
        />
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="scaledTemp"
          stroke="#ffa657"
          strokeWidth={CHART_CONFIG.strokeWidth}
          strokeDasharray="5 5"
          dot={false}
          name="Avg Temperature"
          connectNulls={true}
          isAnimationActive={animationsEnabled}
          animationDuration={CHART_CONFIG.animation.duration}
        />
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="scaledWind"
          stroke="#58a6ff"
          strokeWidth={CHART_CONFIG.strokeWidth}
          strokeDasharray="5 5"
          dot={false}
          name="Avg Wind Speed"
          connectNulls={true}
          isAnimationActive={animationsEnabled}
          animationDuration={CHART_CONFIG.animation.duration}
        />
      </LineChart>
    </ResponsiveContainer>
  );
});

export function OutageTimelineChart() {
  const { startDate, endDate } = useDateRange();
  const { data: outageResponse, isLoading: outageLoading } = useOutageHistory(startDate, endDate);
  const { data: communityResponse, isLoading: communityLoading } = useCommunityHistory(startDate, endDate);
  const { data: weatherResponse } = useWeatherHistory(startDate, endDate);

  // Defensive: handle both old (raw array) and new ({ data, meta }) API formats
  const outageHistory = Array.isArray(outageResponse)
    ? outageResponse
    : (outageResponse?.data ?? []);
  const communityHistory = Array.isArray(communityResponse)
    ? communityResponse
    : (communityResponse?.data ?? []);
  const weatherHistory = Array.isArray(weatherResponse)
    ? weatherResponse
    : (weatherResponse?.data ?? []);

  const chartData = useMemo(() => {
    // Pre-compute today's date string once for all formatting calls
    const todayDateStr = new Date().toLocaleDateString("en-US", { timeZone: "America/Halifax" });

    // Build Map for O(1) community lookup by timestamp - O(n) once instead of O(n²)
    const communityByTimestamp = new Map<string, number>();
    communityHistory.forEach((c: CommunityTimeSeries) => {
      const current = communityByTimestamp.get(c.timestamp) || 0;
      communityByTimestamp.set(c.timestamp, current + c.outageCount);
    });

    // Pre-sort weather data by timestamp for binary search - O(n log n) once
    const sortedWeather = weatherHistory
      .map((w: WeatherAverage) => ({
        timestamp: new Date(w.timestamp).getTime(),
        data: w,
      }))
      .sort((a, b) => a.timestamp - b.timestamp);

    // Single pass through outage data - O(n) with O(1) lookups and O(log n) weather search
    return outageHistory.map((outagePoint: { timestamp: string }) => {
      const totalCustomersOut = communityByTimestamp.get(outagePoint.timestamp) || 0;

      const dataPoint: {
        timestamp: string;
        time: string;
        outages: number;
        avgTemp?: number;
        avgWind?: number;
        scaledTemp?: number;
        scaledWind?: number;
      } = {
        timestamp: outagePoint.timestamp,
        time: formatChartTime(outagePoint.timestamp, todayDateStr),
        outages: totalCustomersOut,
      };

      // Find nearest weather reading using binary search - O(log n)
      const outageTime = new Date(outagePoint.timestamp).getTime();
      const nearestWeather = findNearestWeather(sortedWeather, outageTime);

      if (nearestWeather) {
        const scaledTemp =
          nearestWeather.avgTemperature != null
            ? nearestWeather.avgTemperature * 3
            : undefined;
        const scaledWind =
          nearestWeather.avgWindSpeed != null
            ? nearestWeather.avgWindSpeed * 2
            : undefined;

        dataPoint.avgTemp = nearestWeather.avgTemperature;
        dataPoint.avgWind = nearestWeather.avgWindSpeed;
        dataPoint.scaledTemp = scaledTemp;
        dataPoint.scaledWind = scaledWind;
      }

      return dataPoint;
    });
  }, [outageHistory, weatherHistory, communityHistory]);

  // Determine if animations should be enabled based on data size
  const animationsEnabled = shouldAnimate(chartData.length);

  return (
    <Card className="chart-container">
      <FullscreenChart title="Outage Count Over Time">
        <h3>Outage Count Over Time</h3>
        {outageLoading || communityLoading ? (
          <LoadingSkeleton variant="chart" />
        ) : chartData.length > 0 ? (
          <OutageChartContent chartData={chartData} animationsEnabled={animationsEnabled} />
        ) : (
          <p className="no-data">No data for selected range</p>
        )}
      </FullscreenChart>
    </Card>
  );
}
